import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUpdateRole } from '../hooks/useUpdateRole';
import { AlertTriangle, Shield, ShieldCheck, X } from 'lucide-react';
import { usePermissions } from 'features/permission/hooks/usePermissions';

const roleSchema = z.object({
  name: z
    .string()
    .min(1, 'Role name is required')
    .regex(/^[a-z_]+$/, 'Role name can only contain lowercase letters and underscores')
    .transform((val) => val.toLowerCase()),
  description: z.string().max(500, 'Description cannot exceed 500 characters').optional(),
  permissions: z.array(z.string()).min(1, 'At least one permission is required'),
  isActive: z.boolean().optional(),
});

const EditRoleModal = (props) => {
  const { isEditModalOpen, setIsEditModalOpen, role } = props;
  const { mutate: updateRole, isPending } = useUpdateRole();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: permissions } = usePermissions({ limit: 99, isActive: true });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roleSchema),
    mode: 'onChange',
  });

  const selectedPermissions = watch('permissions') || [];

  useEffect(() => {
    if (role) {
      reset({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions?.map((p) => (typeof p === 'string' ? p : p._id)) || [],
        isActive: role.isActive,
      });
    }
  }, [role, reset]);

  const onSubmit = (data) => {
    if (role._id) {
      updateRole(
        { roleId: role._id, ...data },
        {
          onSuccess: () => {
            reset();
            setIsEditModalOpen(false);
          },
        }
      );
    }
  };

  // Filter permissions based on search
  const filteredPermissions = permissions?.data?.filter((permission) =>
    permission.module.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getActionColor = (action) => {
    const colors = {
      view: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      create: 'bg-green-500/10 text-green-600 dark:text-green-400',
      edit: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      delete: 'bg-red-500/10 text-red-600 dark:text-red-400',
      approve: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      export: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
      import: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    };
    return colors[action] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  if (!isEditModalOpen) return null;

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto custom-scrollbar'>
        {/* Header */}
        <div className='sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10'>
          <div>
            <h2 className='text-lg font-semibold'>Edit Role</h2>
            {role?.isSystemRole && (
              <div className='flex items-center space-x-1 text-xs text-orange-600 dark:text-orange-400 mt-1'>
                <Shield size={12} />
                <span>System Role</span>
              </div>
            )}
          </div>
          <button onClick={() => setIsEditModalOpen(false)} className='p-1 hover:bg-muted rounded-md transition-colors'>
            <X size={20} />
          </button>
        </div>

        {/* System Role Warning */}
        {role?.isSystemRole && (
          <div className='mx-6 mt-4 bg-orange-500/10 border border-orange-500/20 rounded-lg p-3'>
            <div className='flex items-start space-x-2'>
              <AlertTriangle size={16} className='text-orange-600 dark:text-orange-400 mt-0.5' />
              <div className='text-xs text-orange-600 dark:text-orange-400'>
                <strong>System Role:</strong> You can only modify permissions and description. Role name cannot be
                changed.
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>
          {/* Role Name */}
          <div>
            <label className='block text-sm font-medium mb-1'>
              Role Name <span className='text-destructive'>*</span>
            </label>
            <input
              type='text'
              {...register('name')}
              className='w-full px-3 py-2 text-sm'
              disabled={role?.isSystemRole}
            />
            {errors.name && <p className='text-destructive text-xs mt-1'>{errors.name.message}</p>}
            {role?.isSystemRole ? (
              <p className='text-xs text-muted-foreground mt-1'>System role names cannot be modified</p>
            ) : (
              <p className='text-xs text-muted-foreground mt-1'>Only lowercase letters and underscores allowed</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className='block text-sm font-medium mb-1'>Description</label>
            <textarea {...register('description')} rows={3} className='w-full px-3 py-2 text-sm' maxLength={500} />
            {errors.description && <p className='text-destructive text-xs mt-1'>{errors.description.message}</p>}
            <p className='text-xs text-muted-foreground mt-1'>Maximum 500 characters</p>
          </div>

          {/* Permissions Selection */}
          <div>
            <label className='block text-sm font-medium mb-2'>
              Permissions <span className='text-destructive'>*</span>
            </label>

            {/* Search Permissions */}
            <div className='mb-3'>
              <input
                type='text'
                placeholder='Search permissions...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full px-3 py-2 text-sm'
              />
            </div>

            {/* Permissions List */}
            <div className='space-y-2 max-h-80 overflow-y-auto custom-scrollbar border border-border rounded-lg p-3'>
              {filteredPermissions && filteredPermissions.length > 0 ? (
                filteredPermissions.map((permission) => (
                  <label
                    key={permission._id}
                    className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedPermissions.includes(permission._id)
                        ? 'bg-primary/10 border-primary'
                        : 'border-border hover:bg-accent'
                    }`}
                  >
                    <input
                      type='checkbox'
                      value={permission._id}
                      {...register('permissions')}
                      className='mt-1 w-4 h-4 rounded'
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center space-x-2 mb-1'>
                        <div className='text-sm font-medium'>{permission.module}</div>
                        <div className='text-xs text-muted-foreground'>({permission.slug})</div>
                      </div>
                      {permission.description && (
                        <div className='text-xs text-muted-foreground mb-2'>{permission.description}</div>
                      )}
                      <div className='flex flex-wrap gap-1'>
                        {permission.actions?.map((action) => (
                          <span key={action} className={`px-1.5 py-0.5 text-xs rounded ${getActionColor(action)}`}>
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  </label>
                ))
              ) : (
                <div className='text-center py-8 text-muted-foreground text-sm'>
                  {searchQuery ? 'No permissions found matching your search' : 'No permissions available'}
                </div>
              )}
            </div>
            {errors.permissions && <p className='text-destructive text-xs mt-1'>{errors.permissions.message}</p>}
          </div>

          {/* Selected Permissions Summary */}
          {selectedPermissions.length > 0 && (
            <div className='bg-blue-500/10 border border-blue-500/20 rounded-lg p-4'>
              <div className='flex items-center space-x-2 mb-2'>
                <Shield size={16} className='text-blue-600 dark:text-blue-400' />
                <span className='text-sm font-medium text-blue-600 dark:text-blue-400'>
                  Selected Permissions: {selectedPermissions.length}
                </span>
              </div>
              <div className='flex flex-wrap gap-2'>
                {permissions?.data
                  ?.filter((p) => selectedPermissions.includes(p._id))
                  .map((permission) => (
                    <span
                      key={permission._id}
                      className='px-2 py-1 bg-primary/10 text-primary text-xs rounded-md font-medium'
                    >
                      {permission.module}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Active Status */}
          <div>
            <label className='flex items-center space-x-2 cursor-pointer'>
              <input type='checkbox' {...register('isActive')} className='w-4 h-4 rounded' />
              <span className='text-sm'>Active</span>
            </label>
          </div>

          {/* Actions */}
          <div className='flex justify-end space-x-3 pt-4 border-t border-border'>
            <button
              type='button'
              onClick={() => setIsEditModalOpen(false)}
              className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={isPending}
            >
              {isPending ? (
                <div className='flex items-center justify-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2' />
                  Updating...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <ShieldCheck size={16} className='mr-2' />
                  Update Role
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRoleModal;
